// Constraints of the analyst in the tool (todo/10, Step 2): which attributes keep their position, which category
// orders are fixed (ordinal order, left or right direction), which categories are pinned, and which adjacent
// categories form a block. No DOM. The state holds names only; positions and orders are read from the
// configuration that is shown, so a constraint always describes the current plot ("as shown"). The tool sets them
// through context menus on the plot (todo/11); the three first-attribute modes are states of the constraints:
// user-fixed = the first axis has a fixed position, automatic = the highest-entropy axis was moved first and fixed
// (moveFirst), unconstrained = no fixed position.
import { buildStructure, isAdmissible } from './optimizers/constraints.js';

const SEPARATOR = '\u0000';
export const ORDER_STATES = ['free', 'left', 'right'];

export function createState() {
    // positions: attribute names; order: attribute -> 'left' | 'right'; pins: attribute -> Set of categories;
    // links: attribute -> Set of "a\0b" (a directly left of b; chains of links are blocks). The maps have no
    // prototype: attribute names such as "constructor" or "toString" are ordinary keys.
    return { positions: new Set(), order: Object.create(null), pins: Object.create(null), links: Object.create(null) };
}

const linkKey = (a, b) => a + SEPARATOR + b;
const hasLink = (state, attribute, a, b) => !!state.links[attribute] && state.links[attribute].has(linkKey(a, b));

// Blocks of an attribute ({ name, categories: [names] } as shown): maximal runs of linked neighbors.
export function blocksOf(state, attribute) {
    const blocks = [];
    let run = [attribute.categories[0]];
    for (let i = 1; i <= attribute.categories.length; i++) {
        if (i < attribute.categories.length && hasLink(state, attribute.name, attribute.categories[i - 1], attribute.categories[i])) {
            run.push(attribute.categories[i]);
        } else {
            if (run.length > 1) blocks.push(run);
            run = [attribute.categories[i]];
        }
    }
    return blocks;
}

// 'blocks' if the attribute has a block (blocks supersede the ordinal order), else 'free' | 'left' | 'right'.
export function orderState(state, attribute) {
    if (blocksOf(state, attribute).length > 0) return 'blocks';
    return state.order[attribute.name] || 'free';
}

// Constraint of one attribute in the form of optimizers/constraints.js, or null.
export function categoryConstraint(state, attribute) {
    const constraint = {};
    const blocks = blocksOf(state, attribute);
    if (blocks.length > 0) constraint.blocks = blocks;
    else if (state.order[attribute.name]) constraint.order = attribute.categories.slice();
    const pins = [...(state.pins[attribute.name] || [])].filter(c => attribute.categories.includes(c));
    if (pins.length > 0) constraint.pins = Object.fromEntries(pins.map(c => [c, attribute.categories.indexOf(c)]));
    return Object.keys(constraint).length > 0 ? constraint : null;
}

// Field `constraints` of the request to optimizers/optimize.js.
export function toRequest(state, config) {
    const positions = {}, categories = {};
    config.forEach((attribute, index) => {
        if (state.positions.has(attribute.name)) positions[attribute.name] = index;
        const constraint = categoryConstraint(state, attribute);
        if (constraint) categories[attribute.name] = constraint;
    });
    return { positions, categories };
}

// Drops what the configuration shown does not support: unknown names and links of categories that are no
// longer neighbors (e.g., after a lookup replaced the configuration).
export function reconcile(state, config) {
    const byName = new Map(config.map(a => [a.name, a]));
    for (const name of [...state.positions]) if (!byName.has(name)) state.positions.delete(name);
    for (const key of ['order', 'pins', 'links']) for (const name of Object.keys(state[key])) if (!byName.has(name)) delete state[key][name];
    for (const [name, pins] of Object.entries(state.pins)) for (const c of [...pins]) if (!byName.get(name).categories.includes(c)) pins.delete(c);
    for (const [name, links] of Object.entries(state.links)) {
        const categories = byName.get(name).categories;
        for (const key of [...links]) {
            const [a, b] = key.split(SEPARATOR);
            const i = categories.indexOf(a);
            if (i < 0 || categories[i + 1] !== b) links.delete(key);
        }
    }
    return state;
}

// True if a new category order of an attribute (as shown before: attribute) keeps its constraint.
export function allowsCategories(state, attribute, proposed) {
    const constraint = categoryConstraint(state, attribute);
    if (!constraint) return true;
    const structure = buildStructure(attribute.categories, constraint);
    return isAdmissible(structure, Int32Array.from(attribute.categories, c => proposed.indexOf(c)));
}

// True if a new attribute order keeps every fixed attribute at its position.
export function allowsAttributes(state, config, proposed) {
    return config.every((attribute, index) => !state.positions.has(attribute.name) || proposed[index] === attribute.name);
}

export function togglePosition(state, name) {
    if (!state.positions.delete(name)) state.positions.add(name);
}

export function togglePin(state, attribute, category) {
    const pins = state.pins[attribute] || (state.pins[attribute] = new Set());
    if (!pins.delete(category)) pins.add(category);
}

// Link between two neighbors (a directly left of b).
export function toggleLink(state, attribute, a, b) {
    const links = state.links[attribute] || (state.links[attribute] = new Set());
    if (!links.delete(linkKey(a, b))) links.add(linkKey(a, b));
}

// Sets the ordinal state of an attribute; returns its categories in the order to show: 'left' keeps the order
// shown, 'right' is its reverse, and a change between the two directions reverses the order.
export function setOrder(state, attribute, value) {
    if (!ORDER_STATES.includes(value)) throw new Error('Unknown order state: ' + value);
    const before = state.order[attribute.name] || 'free';
    if (value === 'free') delete state.order[attribute.name];
    else state.order[attribute.name] = value;
    const reverse = value !== 'free' && value !== before && (before !== 'free' || value === 'right');
    return reverse ? attribute.categories.slice().reverse() : attribute.categories.slice();
}

// --- Actions of the context menus (todo/11): categories of one attribute as shown ({ name, categories }) -------

// True if the categories (names) are neighbors in the order shown.
export function isContiguous(attribute, categories) {
    const indexes = categories.map(c => attribute.categories.indexOf(c)).sort((a, b) => a - b);
    return indexes.length > 0 && indexes[0] >= 0 && indexes.every((index, i) => index === indexes[0] + i);
}

// Pins (attribute: name, as togglePin): each category at its slot.
export function pinAll(state, attribute, categories) {
    const pins = state.pins[attribute] || (state.pins[attribute] = new Set());
    categories.forEach(c => pins.add(c));
}

export function releasePins(state, attribute, categories) {
    if (state.pins[attribute]) categories.forEach(c => state.pins[attribute].delete(c));
}

// Links contiguous categories into one block (with the links they already have).
export function linkBlock(state, attribute, categories) {
    if (!isContiguous(attribute, categories)) throw new Error('A block needs adjacent categories.');
    const links = state.links[attribute.name] || (state.links[attribute.name] = new Set());
    const shown = attribute.categories.filter(c => categories.includes(c));
    for (let i = 1; i < shown.length; i++) links.add(linkKey(shown[i - 1], shown[i]));
}

// Removes every block that contains one of the categories.
export function releaseBlocks(state, attribute, categories) {
    for (const block of blocksOf(state, attribute)) {
        if (!block.some(c => categories.includes(c))) continue;
        for (let i = 1; i < block.length; i++) state.links[attribute.name].delete(linkKey(block[i - 1], block[i]));
    }
}

// Releases position, order, pins, and blocks of one attribute.
export function releaseAxis(state, name) {
    state.positions.delete(name);
    for (const key of ['order', 'pins', 'links']) delete state[key][name];
}

export function releaseAll(state) {
    Object.assign(state, createState());
}

// Filter of the lookups (field firstDimension, todo/11 Decision 3): the first attribute and its order as shown if
// both its position and its order are fixed, else null (no filter).
export function lookupFirstDimension(state, config) {
    const first = config[0];
    return state.positions.has(first.name) && state.order[first.name] ? { name: first.name, categories: first.categories.slice() } : null;
}

// Attribute order (names) with the attribute moved to the first position, the others shifted right; null if a
// fixed attribute stands before it (the shift would move it).
export function moveFirst(state, config, name) {
    const index = config.findIndex(a => a.name === name);
    if (index < 0 || config.slice(0, index).some(a => state.positions.has(a.name))) return null;
    return [name, ...config.map(a => a.name).filter(n => n !== name)];
}

// Number of attribute orders that keep the fixed positions.
export function attributeOrderCount(state, config) {
    let count = 1;
    for (let i = 2; i <= config.length - state.positions.size; i++) count *= i;
    return count;
}
